import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useReducer, useRef } from "react"
import { twMerge } from "tailwind-merge"
import { Logger } from "./logger.ts"
import { ScrollBar, type ScrollBarTapCircleOptions, TAP_SCROLL_CANCEL_EVENT } from "./ScrollBar.tsx"
import { minmax } from "./utils.ts"

/**
 * Props for the ScrollPane component.
 *
 * ScrollPane コンポーネントの Props。
 */
export type ScrollPaneProps = {
    /**
     * A function that renders the content of the scroll pane.
     * It receives the current scroll position as an argument.
     *
     * スクロールペインのコンテンツをレンダリングする関数です。
     * 現在のスクロール位置を引数として受け取ります。
     */
    children: (scrollPosition: number) => React.ReactNode
    /** The total size of the content. / コンテンツの総サイズ。 */
    contentSize: number
    /** The size of the visible area. / 表示領域のサイズ。 */
    viewportSize: number
    /** The width of the scrollbar. / スクロールバーの幅。 */
    scrollBarWidth?: number
    /** Whether grabbing the scrollbar thumb is allowed. / スクロールバーのつまみ操作を許可するかどうか。 */
    enableThumbDrag?: boolean
    /** A callback function that is called when the scroll position changes. / スクロール位置が変更されたときに呼び出されるコールバック関数。 */
    onScroll?: (scrollPosition: number, prevPosition: number) => void
    /** Additional class names for the component. / コンポーネントの追加のクラス名。 */
    className?: string
    /** Custom styles for the component. / コンポーネントのカスタムスタイル。 */
    style?: React.CSSProperties
    /** A background element to be rendered behind the content. / コンテンツの背後にレンダリングされる背景要素。 */
    background?: React.ReactNode
    /** Configuration for the tap scroll circle. / タップサークルの設定。 */
    tapScrollCircleOptions?: ScrollBarTapCircleOptions
    /** Configuration for inertia scroll behavior. / 慣性スクロールの設定。 */
    inertiaOptions?: ScrollPaneInertiaOptions
    /** Total number of scrollable items (optional). / スクロール対象アイテムの総数（任意）。 */
    itemCount?: number
    /** Whether clicking the scrollbar track moves the thumb. / スクロールバートラックのクリック操作を許可するかどうか。 */
    enableTrackClick?: boolean
    /** Whether arrow buttons control the scroll position. / 矢印ボタンのスクロール操作を許可するかどうか。 */
    enableArrowButtons?: boolean
    /** Whether dragging the content area scrolls the pane. / コンテンツ領域のドラッグでスクロールさせるかどうか。 */
    enablePointerDrag?: boolean
}

export type ScrollPaneInertiaOptions = {
    /** Maximum velocity applied when inertia starts. / 慣性開始時に適用する最大速度。 */
    maxVelocity?: number
    /** Minimum velocity threshold to continue inertia. / 慣性を継続するための最小速度しきい値。 */
    minVelocity?: number
    /** Deceleration applied on every frame. / 各フレームで適用される減速度。 */
    deceleration?: number
    /** Sampling window in milliseconds for pointer velocity calculation. / ポインタの速度計算に用いるミリ秒単位のサンプリング期間。 */
    velocitySampleWindow?: number
    /** Minimum velocity required to start inertia. / 慣性を開始するために必要な最小速度。 */
    startVelocityThreshold?: number
}

type ResolvedScrollPaneInertiaOptions = Required<ScrollPaneInertiaOptions>

const DEFAULT_INERTIA_OPTIONS: ResolvedScrollPaneInertiaOptions = {
    maxVelocity: 6,
    minVelocity: 0.02,
    deceleration: 0.0025,
    velocitySampleWindow: 90,
    startVelocityThreshold: 0.04,
}

type ListenerEntry = [string, EventListenerOrEventListenerObject, AddEventListenerOptions | boolean | undefined]

/**
 * Toggles event listeners on a given target.
 *
 * 指定ターゲットにイベントリスナーを追加または削除。
 */
const toggleListeners = (target: HTMLElement | Window, entries: ListenerEntry[], action: "add" | "remove") => {
    for (const [type, listener, options] of entries) {
        if (action === "add") {
            target.addEventListener(type, listener, options)
        } else {
            target.removeEventListener(type, listener, options)
        }
    }
}

export type ScrollPaneHandle = {
    scrollTo: (newPosition: number) => void
    getScrollPosition: () => number
    getContentSize: () => number
    getViewportSize: () => number
}

/**
 * A component that provides a scrollable view with a custom scrollbar.
 *
 * カスタムスクロールバーを備えたスクロール可能なビューを提供するコンポーネントです。
 */
export const ScrollPane = forwardRef<ScrollPaneHandle, ScrollPaneProps>(({ children, contentSize, viewportSize, scrollBarWidth = 12, enableThumbDrag = true, enableTrackClick = true, enableArrowButtons = true, enablePointerDrag = true, onScroll, className, style, background, tapScrollCircleOptions, inertiaOptions, itemCount }, ref) => {
    const scrollPositionRef = useRef(0)
    const [_, _forceUpdate] = useReducer((x) => x + 1, 0)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const contentAreaRef = useRef<HTMLDivElement>(null)
    const inertiaStateRef = useRef<{ frame: number | null; velocity: number; lastTimestamp: number | null }>({
        frame: null,
        velocity: 0,
        lastTimestamp: null,
    })

    const resolvedInertiaOptions = useMemo<ResolvedScrollPaneInertiaOptions>(
        () => ({
            maxVelocity: inertiaOptions?.maxVelocity ?? DEFAULT_INERTIA_OPTIONS.maxVelocity,
            minVelocity: inertiaOptions?.minVelocity ?? DEFAULT_INERTIA_OPTIONS.minVelocity,
            deceleration: inertiaOptions?.deceleration ?? DEFAULT_INERTIA_OPTIONS.deceleration,
            velocitySampleWindow: inertiaOptions?.velocitySampleWindow ?? DEFAULT_INERTIA_OPTIONS.velocitySampleWindow,
            startVelocityThreshold: inertiaOptions?.startVelocityThreshold ?? DEFAULT_INERTIA_OPTIONS.startVelocityThreshold,
        }),
        [inertiaOptions],
    )

    Logger.debug("[ScrollPane] ScrollPane rendered", { contentSize, viewportSize, scrollBarWidth, className, style, tapScrollCircleOptions, inertiaOptions, enablePointerDrag })

    // const size = useMemo(() => ({ contentSize, viewportSize }), [contentSize, viewportSize])

    // contentSize と viewportSize を ref として保持
    // const sizeRef = useRef({ contentSize, viewportSize })

    const sizeRef = useRef({ contentSize, viewportSize })

    const isScrollable = useMemo(() => contentSize > viewportSize, [contentSize, viewportSize])

    // useEffect(() => {  // useEffect では contentSize や viewportSize の変更が反映される前にレンダリングされて位置がズレる可能性があるため、useLayoutEffect を使用
    //     Logger.debug("[ScrollPane] ScrollPane size updated", { contentSize, viewportSize })
    //     // サイズが変更されたときに ref を更新
    //     sizeRef.current = { contentSize, viewportSize }
    //     forceUpdate() // サイズが変更されたときに強制的に再レンダリング
    // }, [contentSize, viewportSize])

    // const scrollTo = useCallback(
    //     (newPosition: number | ((prev: number) => number)) => {
    //         const { contentSize: currentContentSize, viewportSize: currentViewportSize } = sizeRef.current
    //         const currentIsScrollable = currentContentSize > currentViewportSize
    //         const prevPosition = scrollPositionRef.current

    //         Logger.debug("[ScrollPane] scrollTo called", { newPosition, currentContentSize, currentViewportSize, currentIsScrollable, prevPosition })

    //         if (!currentIsScrollable) {
    //             // スクロール不可の場合は常に0に
    //             if (scrollPositionRef.current !== 0) {
    //                 scrollPositionRef.current = 0
    //                 onScroll?.(0, prevPosition)
    //             }
    //             return
    //         }
    //         const nextPosition = typeof newPosition === "function" ? newPosition(scrollPositionRef.current) : newPosition
    //         const newScrollPosition = minmax(nextPosition, 0, currentContentSize - currentViewportSize)
    //         if (scrollPositionRef.current !== newScrollPosition) {
    //             scrollPositionRef.current = newScrollPosition
    //             onScroll?.(newScrollPosition, prevPosition)
    //         }
    //     },
    //     [onScroll]
    // )

    const scrollTo = useCallback(
        (newPosition: number | ((prev: number) => number)) => {
            const { contentSize: currentContentSize, viewportSize: currentViewportSize } = sizeRef.current
            const currentIsScrollable = currentContentSize > currentViewportSize
            const prevPosition = scrollPositionRef.current

            Logger.debug("[ScrollPane] scrollTo called", { newPosition, contentSize: currentContentSize, viewportSize: currentViewportSize, currentIsScrollable, prevPosition })

            if (!currentIsScrollable) {
                // スクロール不可の場合は常に0に
                if (scrollPositionRef.current !== 0) {
                    scrollPositionRef.current = 0
                    onScroll?.(0, prevPosition)
                }
                return
            }
            const nextPosition = typeof newPosition === "function" ? newPosition(scrollPositionRef.current) : newPosition
            const newScrollPosition = minmax(nextPosition, 0, currentContentSize - currentViewportSize)
            if (scrollPositionRef.current !== newScrollPosition) {
                scrollPositionRef.current = newScrollPosition
                onScroll?.(newScrollPosition, prevPosition)
            }
        },
        [onScroll],
    )

    const stopInertia = useCallback(() => {
        const state = inertiaStateRef.current
        if (state.frame !== null) {
            cancelAnimationFrame(state.frame)
        }
        state.frame = null
        state.velocity = 0
        state.lastTimestamp = null
    }, [])

    const startInertia = useCallback(
        (initialVelocity: number) => {
            if (!isScrollable) {
                return
            }

            const { maxVelocity, minVelocity, deceleration, startVelocityThreshold } = resolvedInertiaOptions

            const limitedVelocity = minmax(initialVelocity, -maxVelocity, maxVelocity)
            if (Math.abs(limitedVelocity) < startVelocityThreshold) {
                return
            }

            stopInertia()

            inertiaStateRef.current.velocity = limitedVelocity
            inertiaStateRef.current.lastTimestamp = null

            const step = (timestamp: number) => {
                const state = inertiaStateRef.current
                if (state.lastTimestamp === null) {
                    state.lastTimestamp = timestamp
                    state.frame = requestAnimationFrame(step)
                    return
                }

                const deltaTime = timestamp - state.lastTimestamp
                state.lastTimestamp = timestamp

                if (deltaTime <= 0) {
                    state.frame = requestAnimationFrame(step)
                    return
                }

                const previousVelocity = state.velocity
                let nextVelocity = previousVelocity
                const decelerationAmount = deceleration * deltaTime
                if (previousVelocity > 0) {
                    nextVelocity = Math.max(0, previousVelocity - decelerationAmount)
                } else if (previousVelocity < 0) {
                    nextVelocity = Math.min(0, previousVelocity + decelerationAmount)
                }

                const averageVelocity = (previousVelocity + nextVelocity) / 2
                const distance = averageVelocity * deltaTime
                const previousPosition = scrollPositionRef.current

                if (distance !== 0) {
                    scrollTo((prevPositionInternal) => prevPositionInternal + distance)
                }

                const nextPosition = scrollPositionRef.current
                const { contentSize: currentContentSize, viewportSize: currentViewportSize } = sizeRef.current
                const maxScrollPosition = Math.max(currentContentSize - currentViewportSize, 0)

                state.velocity = nextVelocity

                const reachedBoundary = nextPosition === previousPosition || (nextPosition <= 0 && nextVelocity <= 0) || (nextPosition >= maxScrollPosition && nextVelocity >= 0)

                if (Math.abs(nextVelocity) < minVelocity || reachedBoundary) {
                    stopInertia()
                    return
                }

                state.frame = requestAnimationFrame(step)
            }

            inertiaStateRef.current.frame = requestAnimationFrame(step)
        },
        [isScrollable, resolvedInertiaOptions, scrollTo, stopInertia],
    )

    useLayoutEffect(() => {
        // contentSize と viewportSize の最新値を保持して scrollTo の参照を安定させる
        sizeRef.current = { contentSize, viewportSize }
    }, [contentSize, viewportSize])

    // contentSize または viewportSize が変更されたときにスクロール位置を調整します。
    useLayoutEffect(() => {
        // useEffect では contentSize や viewportSize の変更が反映される前にレンダリングされて位置がズレる可能性があるため、useLayoutEffect を使用。フリッカー防止も。
        if (isScrollable) {
            Logger.debug("[ScrollPane] Adjusting scroll position due to content or viewport size change", { contentSize, viewportSize, scrollPosition: scrollPositionRef.current })
            const maxScrollPosition = minmax(contentSize - viewportSize, 0, contentSize)
            if (scrollPositionRef.current > maxScrollPosition) {
                scrollTo(maxScrollPosition)
            }
        } else {
            scrollTo(0)
        }
    }, [isScrollable, scrollTo, contentSize, viewportSize])

    // useEffect(() => {
    //     // ホイールイベントのハンドラ
    //     const handleWheel = (event: WheelEvent) => {
    //         if (!isScrollable) {
    //             return
    //         }

    //         event.preventDefault()

    //         let deltaY = event.deltaY

    //         // deltaMode に応じてスクロール量を調整
    //         if (event.deltaMode === 1) {
    //             // DOM_DELTA_LINE: 行単位のスクロール
    //             const lineHeight = 16 // 1行のおおよその高さ (ピクセル)
    //             deltaY *= lineHeight
    //         } else if (event.deltaMode === 2) {
    //             // DOM_DELTA_PAGE: ページ単位のスクロール
    //             deltaY *= sizeRef.current.viewportSize // ビューポートの高さを基準にスクロール
    //         }

    //         Logger.debug("[ScrollPane] wheel event", { deltaY, scrollPosition: scrollPositionRef.current })

    //         // スクロール位置を更新
    //         scrollTo((prev) => prev + deltaY)
    //     }

    //     const scrollContainer = scrollContainerRef.current
    //     if (scrollContainer) {
    //         // wheel イベントリスナーを passive: false で登録し、preventDefault を可能にする
    //         scrollContainer.addEventListener("wheel", handleWheel, { passive: false })
    //     }

    //     // クリーンアップ関数
    //     return () => {
    //         if (scrollContainer) {
    //             scrollContainer.removeEventListener("wheel", handleWheel)
    //         }
    //     }
    // }, [isScrollable, scrollTo])

    useEffect(() => {
        // ホイールイベントのハンドラ
        const handleWheel = (event: WheelEvent) => {
            if (!isScrollable) {
                return
            }

            event.preventDefault()

            stopInertia()

            let deltaY = event.deltaY

            // deltaMode に応じてスクロール量を調整
            if (event.deltaMode === 1) {
                // DOM_DELTA_LINE: 行単位のスクロール
                const lineHeight = 16 // 1行のおおよその高さ (ピクセル)
                deltaY *= lineHeight
            } else if (event.deltaMode === 2) {
                // DOM_DELTA_PAGE: ページ単位のスクロール
                deltaY *= viewportSize // ビューポートの高さを基準にスクロール
            }

            Logger.debug("[ScrollPane] wheel event", { deltaY, scrollPosition: scrollPositionRef.current })

            // スクロール位置を更新
            scrollTo((prev) => prev + deltaY)
        }

        const scrollContainer = scrollContainerRef.current
        if (scrollContainer) {
            // wheel イベントリスナーを passive: false で登録し、preventDefault を可能にする
            scrollContainer.addEventListener("wheel", handleWheel, { passive: false })
        }

        // クリーンアップ関数
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener("wheel", handleWheel)
            }
        }
    }, [isScrollable, scrollTo, stopInertia, viewportSize])

    useImperativeHandle(
        ref,
        () => ({
            scrollTo,
            getScrollPosition: () => scrollPositionRef.current,
            getContentSize: () => contentSize,
            getViewportSize: () => viewportSize,
        }),
        [scrollTo, contentSize, viewportSize],
    )

    const id = useId()

    useEffect(() => {
        const element = contentAreaRef.current
        if (!element) {
            return
        }

        if (!enablePointerDrag) {
            stopInertia()
            return
        }

        const DRAG_ACTIVATION_THRESHOLD = 6
        let dragPointerId: number | null = null
        let dragStartClientY = 0
        let dragStartScroll = 0
        let isDragging = false
        let shouldCancelNextClick = false
        let clickResetTimer: number | null = null
        let velocitySamples: { clientY: number; time: number }[] = []

        const resetState = () => {
            dragPointerId = null
            dragStartClientY = 0
            dragStartScroll = 0
            isDragging = false
            velocitySamples = []
        }

        const pushVelocitySample = (clientY: number) => {
            const now = performance.now()
            velocitySamples.push({ clientY, time: now })
            velocitySamples = velocitySamples.filter((sample) => now - sample.time <= resolvedInertiaOptions.velocitySampleWindow)
        }

        const shouldIgnoreTarget = (target: EventTarget | null) => target instanceof HTMLElement && target.closest("[data-scrollpane-ignore-drag='true']") !== null

        const handleClickCapture = (event: MouseEvent) => {
            if (!shouldCancelNextClick) {
                return
            }
            event.preventDefault()
            event.stopPropagation()
            shouldCancelNextClick = false
        }

        const startDragging = (event: PointerEvent) => {
            if (isDragging) {
                return
            }
            isDragging = true
            shouldCancelNextClick = true
            if (!element.hasPointerCapture(event.pointerId)) {
                element.setPointerCapture(event.pointerId)
            }
            pushVelocitySample(event.clientY)
        }

        const handlePointerMove = (event: PointerEvent) => {
            if (dragPointerId !== event.pointerId) {
                return
            }

            if (!isDragging) {
                const distanceY = Math.abs(event.clientY - dragStartClientY)
                if (distanceY < DRAG_ACTIVATION_THRESHOLD) {
                    return
                }
                startDragging(event)
                if (!isDragging) {
                    return
                }
            }

            pushVelocitySample(event.clientY)

            const deltaY = event.clientY - dragStartClientY
            const nextPosition = dragStartScroll - deltaY
            scrollTo(nextPosition)

            if (event.cancelable) {
                event.preventDefault()
            }
        }

        const endDrag = (event: PointerEvent) => {
            if (dragPointerId !== event.pointerId) {
                return
            }

            if (isDragging && shouldCancelNextClick && event.cancelable) {
                event.preventDefault()
                event.stopPropagation()
            }

            if (element.hasPointerCapture(event.pointerId)) {
                element.releasePointerCapture(event.pointerId)
            }

            let inertiaVelocity = 0
            if (isDragging && velocitySamples.length >= 2) {
                const lastSample = velocitySamples[velocitySamples.length - 1]
                const firstSample = velocitySamples.find((sample) => lastSample.time - sample.time <= resolvedInertiaOptions.velocitySampleWindow) ?? velocitySamples[0]
                if (lastSample && firstSample && lastSample.time !== firstSample.time) {
                    const deltaClientY = lastSample.clientY - firstSample.clientY
                    const deltaTime = lastSample.time - firstSample.time
                    inertiaVelocity = -(deltaClientY / deltaTime)
                }
            }

            resetState()

            if (clickResetTimer !== null) {
                window.clearTimeout(clickResetTimer)
            }
            if (shouldCancelNextClick) {
                clickResetTimer = window.setTimeout(() => {
                    shouldCancelNextClick = false
                    clickResetTimer = null
                }, 0)
            }

            if (Math.abs(inertiaVelocity) >= resolvedInertiaOptions.startVelocityThreshold) {
                startInertia(inertiaVelocity)
            }
        }

        const handlePointerDown = (event: PointerEvent) => {
            if (!isScrollable) {
                return
            }
            if (event.button !== 0 && event.pointerType === "mouse") {
                return
            }
            if (event.ctrlKey || event.metaKey || event.altKey) {
                return
            }
            if (shouldIgnoreTarget(event.target)) {
                return
            }

            window.dispatchEvent(new CustomEvent(TAP_SCROLL_CANCEL_EVENT, { detail: { paneId: id } }))

            stopInertia()

            dragPointerId = event.pointerId
            dragStartClientY = event.clientY
            dragStartScroll = scrollPositionRef.current
            isDragging = false
            shouldCancelNextClick = false
            velocitySamples = []
        }

        const handlePointerCancel = (event: PointerEvent) => {
            if (dragPointerId !== event.pointerId) {
                return
            }
            shouldCancelNextClick = false
            if (element.hasPointerCapture(event.pointerId)) {
                element.releasePointerCapture(event.pointerId)
            }
            if (clickResetTimer !== null) {
                window.clearTimeout(clickResetTimer)
                clickResetTimer = null
            }
            resetState()
        }

        const elementEntries: ListenerEntry[] = [
            ["click", handleClickCapture as EventListenerOrEventListenerObject, true],
            ["pointerdown", handlePointerDown as EventListenerOrEventListenerObject, { passive: false }],
            ["pointermove", handlePointerMove as EventListenerOrEventListenerObject, { passive: false }],
            ["pointerup", endDrag as EventListenerOrEventListenerObject, undefined],
            ["pointercancel", handlePointerCancel as EventListenerOrEventListenerObject, undefined],
        ]
        const windowEntries: ListenerEntry[] = [
            ["pointermove", handlePointerMove as EventListenerOrEventListenerObject, { passive: false }],
            ["pointerup", endDrag as EventListenerOrEventListenerObject, undefined],
            ["pointercancel", handlePointerCancel as EventListenerOrEventListenerObject, undefined],
        ]

        toggleListeners(element, elementEntries, "add")
        toggleListeners(window, windowEntries, "add")

        return () => {
            toggleListeners(element, elementEntries, "remove")
            toggleListeners(window, windowEntries, "remove")
            if (dragPointerId !== null && element.hasPointerCapture(dragPointerId)) {
                element.releasePointerCapture(dragPointerId)
            }
            if (clickResetTimer !== null) {
                window.clearTimeout(clickResetTimer)
            }
            stopInertia()
        }
    }, [enablePointerDrag, id, isScrollable, resolvedInertiaOptions, scrollTo, startInertia, stopInertia])

    return (
        <div ref={scrollContainerRef} className={twMerge("flex", className)} style={style}>
            <div
                ref={contentAreaRef}
                className="relative h-full flex-1 overflow-hidden"
                style={{ height: viewportSize, ...(enablePointerDrag ? { touchAction: "none" } : {}) }}
                id={id}>
                {background}
                {children(scrollPositionRef.current)}
            </div>
            {isScrollable && (
                <ScrollBar
                    contentSize={contentSize}
                    viewportSize={viewportSize}
                    scrollPosition={scrollPositionRef.current}
                    onScroll={scrollTo}
                    enableThumbDrag={enableThumbDrag}
                    enableTrackClick={enableTrackClick}
                    enableArrowButtons={enableArrowButtons}
                    scrollBarWidth={scrollBarWidth}
                    ariaControls={id}
                    tapScrollCircleOptions={tapScrollCircleOptions}
                    itemCount={itemCount}
                />
            )}
        </div>
    )

    // return (
    //     <div ref={scrollContainerRef} className={twMerge("flex", className)} style={style}>
    //         <div className="relative h-full flex-1 overflow-hidden" style={{ height: viewportSize }} id={id}>
    //             {children(scrollPositionRef.current)}
    //         </div>
    //         {isScrollable && <ScrollBar contentSize={contentSize} viewportSize={sizeRef.current.viewportSize} scrollPosition={scrollPositionRef.current} onScroll={scrollTo} scrollBarWidth={scrollBarWidth} ariaControls={id} />}
    //     </div>
    // )
})
